import React, { useEffect, useMemo, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import FilterPanel from '@components/FilterPanel';
import I18n, { translate } from '@utils/i18n';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import api from '@utils/axios';
import useDebounce from '@hooks/useDebounce';
import { datatableInvoicesPath } from 'routes';
import { saveCacheToLocalStorage } from '@helpers/localStorage';
import kendoDatatableParams from '@helpers/kendoDatatableParams';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import { usePublishListContext } from '@features/AIChatWidget/hooks/usePublishListContext';
import ColumnMenu from './nested/ColumnMenu';
import ColumnMenuContext from './nested/ColumnMenuContext';
import { getInitialData, getInitialFilters } from './helpers/initialData';
import './index.css';
import { actionTypes, reducer } from './reducer';
import { ALL_COLUMNS, ALL_COLUMNS_DATA } from './constants';
import CustomScrollbar from '../../../components/CustomScrollbar/CustomScrollbar';
import MassActions from './nested/MassActions';
import useFilters from './hooks/useFilters';
import { calculateInvoiceNoColumnWidth } from './utils';

const namespace = 'features.invoices.index';

function Index({
  statusesForSelect,
  categoriesForSelect,
  hasShowAllInvoicesInCustomerPortal,
  hideExport,
  hasSageIntegration,
  hasComputereaseIntegration,
  isQbSyncEnabled,
  isRecurrenceEnabled,
  showQbInvoiceNumberColumn,
  buildingId,
  accountId,
  inspectionId,
  workOrderId,
  cacheKey
}) {
  const initialData = useMemo(
    () => getInitialData({ cacheKey, buildingId, accountId, inspectionId, workOrderId, showQbInvoiceNumberColumn }),
    [buildingId, accountId, inspectionId, workOrderId, showQbInvoiceNumberColumn]
  );

  const [state, dispatch] = useReducer(reducer, {
    data: [],
    totalCount: 0,
    ...initialData
  });

  const { page, sort, filters, columns, data, totalCount, loading, selectAll } = state;

  // Publish live grid state to the AI chat widget (page context overlay).
  // Without this the assistant falls back to the server-rendered list_context,
  // which is built at HTML-render time — before this grid has fetched anything
  // — so it carries no filters and an unfiltered count.
  usePublishListContext({ page, sort, filters, totalCount, data });

  const debouncedFilters = useDebounce(filters);

  const filtersList = useFilters({
    filters,
    dispatch,
    statusesForSelect,
    buildingId,
    accountId,
    inspectionId,
    workOrderId,
    categoriesForSelect,
    isQbSyncEnabled,
    isRecurrenceEnabled
  });

  useEffect(() => {
    saveCacheToLocalStorage(cacheKey, { page, sort, filters, columns });
  }, [page, sort, filters, columns]);

  useEffect(() => {
    if (filters !== debouncedFilters) return undefined;

    dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
    const controller = new AbortController();

    api
      .get(datatableInvoicesPath(), {
        params: kendoDatatableParams({
          page,
          sort,
          filters: debouncedFilters
        }),
        signal: controller.signal
      })
      .then((response) => {
        dispatch({ type: actionTypes.DATA_LOADED, data: response.data });
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: false });
      });

    return () => controller.abort();
  }, [page, sort, filters, debouncedFilters]);

  const onSelectionChange = (event) => {
    dispatch({ type: actionTypes.SELECTION_CHANGED, id: event.dataItem.id });
  };

  const onHeaderSelectionChange = (event) => {
    dispatch({ type: actionTypes.HEADER_SELECTION_CHANGED, checked: event.syntheticEvent.target.checked });
  };

  const onSelectAll = () => {
    dispatch({ type: actionTypes.ALL_SELECTED });
  };

  const onColumnsChange = (cols) => {
    dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: cols.map((col) => col.id) });
  };

  const getScreenInfo = () => {
    if (inspectionId) return { screen: 'inspection', screenId: inspectionId };
    if (workOrderId) return { screen: 'workOrder', screenId: workOrderId };
    if (buildingId) return { screen: 'building', screenId: buildingId };
    if (accountId) return { screen: 'account', screenId: accountId };
    return { screen: null, screenId: null };
  };

  const onResetFilters = () => {
    const resetFilters = getInitialFilters(buildingId, accountId, inspectionId, workOrderId);
    dispatch({ type: actionTypes.FILTER_CHANGED, filters: resetFilters });
  };

  const onResetCache = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(I18n.t('generic.are_you_sure'))) return;
    localStorage.removeItem(cacheKey);
    window.location.reload();
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  const invoiceNoColumnWidth = useMemo(
    () => calculateInvoiceNoColumnWidth(data, showQbInvoiceNumberColumn),
    [data, showQbInvoiceNumberColumn]
  );

  const allMenuColumns = useMemo(() => {
    let availableColumns = ALL_COLUMNS;

    const isExcludedColumn = (col) => {
      if (col === 'qbInvoiceNo' && !showQbInvoiceNumberColumn) {
        return true;
      }
      if (inspectionId || workOrderId) {
        return col === 'invoiceFor' || col === 'building' || col === 'account';
      }
      if (buildingId) {
        return col === 'building' || col === 'account';
      }
      if (accountId) {
        return col === 'account';
      }
      return false;
    };

    availableColumns = ALL_COLUMNS.filter((col) => !isExcludedColumn(col));
    return availableColumns.map((column) => ALL_COLUMNS_DATA[column]);
  }, [inspectionId, workOrderId, buildingId, accountId, showQbInvoiceNumberColumn]);

  const selectedMenuColumns = useMemo(
    () =>
      columns
        .filter((column) => (column === 'qbInvoiceNo' ? showQbInvoiceNumberColumn : true))
        .map((column) =>
          column === 'invoiceNo'
            ? { ...ALL_COLUMNS_DATA[column], width: invoiceNoColumnWidth }
            : ALL_COLUMNS_DATA[column]
        ),
    [columns, invoiceNoColumnWidth, showQbInvoiceNumberColumn]
  );

  const headerSelectionValue = selectAll || (totalCount > 0 && data.every((proposal) => proposal.selected));

  const columnMenuContextValue = useMemo(
    () => ({
      onColumnsChange,
      columns: allMenuColumns,
      columnsState: selectedMenuColumns,
      onDataChanged: () => dispatch({ type: actionTypes.DATA_CHANGED }),
      showQbInvoiceNumberColumn,
      ...getScreenInfo()
    }),
    [
      allMenuColumns,
      selectedMenuColumns,
      onColumnsChange,
      showQbInvoiceNumberColumn,
      buildingId,
      accountId,
      inspectionId,
      workOrderId,
      dispatch
    ]
  );

  const queryParams = useMemo(
    () => kendoDatatableParams({ page, sort, filters: debouncedFilters }),
    [page, sort, debouncedFilters]
  );

  return (
    <KendoLocalizationProvider>
      <ColumnMenuContext.Provider value={columnMenuContextValue}>
        <FilterPanel onResetFilters={onResetFilters} onResetCache={onResetCache} filters={filtersList} />

        {loading && (
          <div className="workspace__section">
            <div className="qmb-loading--96" data-title={I18n.t('generic.loading')}>
              <svg role="img">
                <use href="/map.svg#load-spinner" />
              </svg>
            </div>
          </div>
        )}

        {!loading && (
          <CustomScrollbar>
            <Grid
              key={columns.join('-')}
              className="qmb-grid--01"
              data={data}
              total={totalCount}
              skip={page.skip}
              take={page.take}
              onPageChange={(event) => {
                dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
              }}
              pageable={{
                type: 'input',
                pageSizes: [25, 50, 100],
                pageSizeValue: page.take
              }}
              pager={EnterKeyPager}
              sortable
              sort={sort}
              onSortChange={(event) => {
                dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
              }}
              dataItemKey="id"
              selectedField="selected"
              onSelectionChange={onSelectionChange}
              onHeaderSelectionChange={onHeaderSelectionChange}>
              <GridToolbar>
                <MassActions
                  state={state}
                  onSelectAll={onSelectAll}
                  onDataChanged={() => dispatch({ type: actionTypes.DATA_CHANGED })}
                  queryParams={queryParams}
                  hasShowAllInvoicesInCustomerPortal={hasShowAllInvoicesInCustomerPortal}
                  hideExport={hideExport}
                  hasSageIntegration={hasSageIntegration}
                  hasComputereaseIntegration={hasComputereaseIntegration}
                />
              </GridToolbar>

              <Column
                key="selected"
                field="selected"
                sortable={false}
                width={64}
                title={translate('table.headers.selected', { namespace })}
                className="k-table-td --selector"
                headerClassName="k-table-th --selector"
                headerSelectionValue={headerSelectionValue}
              />

              {selectedMenuColumns.map((column) => (
                <Column
                  key={column.id}
                  field={column.field}
                  title={column.title}
                  columnMenu={ColumnMenu}
                  cell={column.cell}
                  sortable={column.sortable}
                  width={isMobile ? column.width || 150 : column.width}
                  format={column.format}
                  className={column.className}
                  headerClassName={column.headerClassName}
                />
              ))}
            </Grid>
          </CustomScrollbar>
        )}
      </ColumnMenuContext.Provider>
    </KendoLocalizationProvider>
  );
}

Index.propTypes = {
  statusesForSelect: PropTypes.array,
  categoriesForSelect: PropTypes.array,
  hasShowAllInvoicesInCustomerPortal: PropTypes.bool,
  hideExport: PropTypes.bool,
  hasSageIntegration: PropTypes.bool,
  hasComputereaseIntegration: PropTypes.bool,
  isQbSyncEnabled: PropTypes.bool,
  isRecurrenceEnabled: PropTypes.bool,
  showQbInvoiceNumberColumn: PropTypes.bool,
  buildingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inspectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  workOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cacheKey: PropTypes.string
};

Index.defaultProps = {
  statusesForSelect: [],
  categoriesForSelect: [],
  hasShowAllInvoicesInCustomerPortal: false,
  hideExport: false,
  hasSageIntegration: false,
  hasComputereaseIntegration: false,
  isQbSyncEnabled: false,
  isRecurrenceEnabled: false,
  showQbInvoiceNumberColumn: false,
  buildingId: null,
  accountId: null,
  inspectionId: null,
  workOrderId: null,
  cacheKey: null
};

export default Index;
